import mockCollaborateUsers from "../mock/collaborateUsers.json";
import mockCollaborateThreads from "../mock/collaborateThreads.json";
import mockCurrentUser from "../mock/currentUser.json";
import { useCollaborateStore } from "../store/useCollaborateStore";
import type { CollaborateUser } from "../types/collaborate";
import type { CreateThreadInput, Thread, ThreadStatus, AnchorDescriptor, InboxSummary } from "../types/collaborateThread";
import type {
  ThreadFilterSummary,
  ThreadListPage,
  ThreadListQuery,
} from "./paginationTypes";
import { COLLABORATE_THREAD_PAGE_SIZE } from "./paginationTypes";
import type { CollaborateApi } from "./types";
import {
  applyCollaborateThreadFilters,
  countThreadsForVersionFilter,
  filterThreadsByListView,
  getThreadVersionNumbers,
} from "../utils/threadFilters";

const MOCK_DELAY_MS = 80;

function delay(): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, MOCK_DELAY_MS));
}

function allMockThreads(): Thread[] {
  return mockCollaborateThreads as Thread[];
}

function mockCurrentUserId(): string {
  return (mockCurrentUser as CollaborateUser).id;
}

function filteredMockThreads(query: ThreadListQuery): Thread[] {
  return applyCollaborateThreadFilters(
    allMockThreads(),
    query.listView,
    query.versionFilter,
    mockCurrentUserId(),
  );
}

function buildMockSummary(
  listView: ThreadListQuery["listView"],
  versionFilter: number | null = null,
): ThreadFilterSummary {
  const threads = allMockThreads();
  const currentUserId = mockCurrentUserId();
  const listThreads = filterThreadsByListView(threads, listView, currentUserId);
  const versionNumbers = getThreadVersionNumbers(listThreads);
  return {
    listViewCounts: {
      comments: countThreadsForVersionFilter(threads, "comments", versionFilter, currentUserId),
      unread: countThreadsForVersionFilter(threads, "unread", versionFilter, currentUserId),
      mentions: countThreadsForVersionFilter(threads, "mentions", versionFilter, currentUserId),
      "all-approvals": countThreadsForVersionFilter(
        threads,
        "all-approvals",
        versionFilter,
        currentUserId,
      ),
      "pending-mine": countThreadsForVersionFilter(
        threads,
        "pending-mine",
        versionFilter,
        currentUserId,
      ),
      "requested-by-me": countThreadsForVersionFilter(
        threads,
        "requested-by-me",
        versionFilter,
        currentUserId,
      ),
    },
    versionCounts: versionNumbers.map((versionNumber) => ({
      versionNumber,
      count: applyCollaborateThreadFilters(threads, listView, versionNumber, currentUserId)
        .length,
    })),
  };
}

function parseMockCursor(cursor: string | null | undefined): number {
  if (!cursor) {
    return 0;
  }
  const offset = Number.parseInt(cursor, 10);
  return Number.isFinite(offset) && offset >= 0 ? offset : 0;
}

function paginateMockThreads(query: ThreadListQuery): ThreadListPage {
  const limit = query.limit ?? COLLABORATE_THREAD_PAGE_SIZE;
  const filtered = filteredMockThreads(query);
  const offset = parseMockCursor(query.cursor);
  const items = filtered.slice(offset, offset + limit);
  const nextOffset = offset + items.length;
  const hasMore = nextOffset < filtered.length;
  return {
    items,
    hasMore,
    nextCursor: hasMore ? String(nextOffset) : null,
    total: filtered.length,
  };
}

export const mockCollaborateApi: CollaborateApi = {
  async fetchUsers(): Promise<CollaborateUser[]> {
    await delay();
    return mockCollaborateUsers as CollaborateUser[];
  },

  async fetchCurrentUser(): Promise<CollaborateUser> {
    await delay();
    return mockCurrentUser as CollaborateUser;
  },

  async fetchThreadSummary(
    _documentId,
    query: Pick<ThreadListQuery, "listView" | "versionFilter"> = {
      listView: "comments",
      versionFilter: null,
    },
  ): Promise<ThreadFilterSummary> {
    await delay();
    return buildMockSummary(query.listView, query.versionFilter);
  },

  async fetchThreadsPage(query: ThreadListQuery): Promise<ThreadListPage> {
    await delay();
    return paginateMockThreads(query);
  },

  async fetchInboxSummary(): Promise<InboxSummary> {
    await delay();
    const threads = allMockThreads();
    const unread = threads.filter((t) => t.unread);
    return {
      unreadMessages: unread.length,
      signoffs: filterThreadsByListView(threads, "pending-mine", mockCurrentUserId()).length,
      mentions: 0,
      latestUnreadRootId: unread[0]?.id ?? null,
      latestPendingSignoffRootId:
        filterThreadsByListView(threads, "pending-mine", mockCurrentUserId())[0]?.id ?? null,
    };
  },

  async getThread(threadId: string): Promise<Thread> {
    await delay();
    const thread = useCollaborateStore.getState().getThread(threadId);
    if (!thread) {
      throw new Error("Thread not found.");
    }
    return thread;
  },

  async createThread(input: CreateThreadInput): Promise<Thread> {
    await delay();
    return useCollaborateStore.getState().addThread(input);
  },

  async addReply(threadId: string, body: string): Promise<void> {
    await delay();
    useCollaborateStore.getState().addReply(threadId, body);
  },

  async patchThreadStatus(threadId: string, status: ThreadStatus): Promise<Thread> {
    await delay();
    useCollaborateStore.getState().setThreadStatus(threadId, status);
    const thread = useCollaborateStore.getState().getThread(threadId);
    if (!thread) {
      throw new Error("Thread not found.");
    }
    return thread;
  },

  async markThreadRead(threadId: string): Promise<void> {
    await delay();
    useCollaborateStore.getState().markThreadRead(threadId);
  },

  async fetchPendingFocus(): Promise<{ rootCommentId: string | null }> {
    await delay();
    return { rootCommentId: null };
  },

  async patchThreadAnchor(threadId: string, anchor: AnchorDescriptor): Promise<Thread> {
    await delay();
    const thread = useCollaborateStore.getState().getThread(threadId);
    if (!thread) {
      throw new Error("Thread not found.");
    }
    const updated = { ...thread, anchor };
    useCollaborateStore.getState().applyThreadFromServer(updated);
    return updated;
  },
};

export function isMockCollaborateApi(api: CollaborateApi): boolean {
  return api === mockCollaborateApi;
}
